import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { X } from "lucide-react";
import { handleApiError } from "../../../common/utils";
import type {
  RCATeamMember,
  TimelineEvent,
  WitnessStatement,
  Consequence,
  PreventiveActionPlan,
  CorrectiveActionPlan,
} from "../../../components/RCA/types";
import { CONTRIBUTING_FACTORS_TEMPLATE as FACTORS_TEMPLATE } from "../../../components/RCA/types";
import { getRCAById, getRCAByIncidentId, updateRCA, updateIncident } from "../services/service";
import { RCAActionModalProps } from "../types/types";
import { getCurrentISODate } from "../utils/utils";
import { useS3Upload, getSignedUrlForS3 } from "../../../utils/s3Client";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import { usePremisesStore, useRegionStore } from "../../../store/store";
import { usePermissions } from "../../../hooks/usePermission";
import { INCIDENTS_MODULE } from "../../../constants/modules";
import GenericRCAPrintPDF from "./GenericRCAPrintPDF";

// Modular Imports
import {
  RCAIntroductionSection,
  RCATeamSection,
  RCASynopsisSection,
  RCATimelineSection,
  RCAWitnessSection,
  RCAConsequencesSection,
  RCARootCauseAnalysisSection,
  RCAContributingFactorsSection,
  RCARootCauseSection,
  RCAConclusionSection,
  RCACorrectiveActionsSection,
  RCAPreventiveActionsSection,
  RCAAttachmentsSection,
  GenericRCAFormData,
} from "./rca-sections";

// ============================================================================
// CONSTANTS
// ============================================================================

const getCurrentDate = (): string => new Date().toISOString().split("T")[0];

const INITIAL_STATE: GenericRCAFormData = {
  organizationName: "",
  branchName: "",
  region: "",
  premisesName: "",
  incidentEventType: "",
  incidentEventTypeOther: "",
  eventDateTime: "",
  rcaDoneDate: getCurrentDate(),
  location: "",
  severity: "",
  reportedBy: "",
  status: "Draft",
  rcaTeamMembers: [],
  incidentSynopsis: "",
  observations: "",
  summary: "",
  identifiedCause: "",
  timelineEvents: [],
  witnessStatements: [],
  consequences: [],
  fishboneAnalysis: { man: [], material: [], machine: [], system: [], management: [] },
  whyWhyAnalysis: [],
  rcaMethod: "Fishbone",
  contributingFactors: FACTORS_TEMPLATE.map((f, i) => ({
    srNo: i + 1,
    factor: f.factor,
    observation: "",
    isApplicable: false,
  })),
  rootCauseIdentified: false,
  rootCauseDescription: "",
  conclusion: "",
  correctiveActionPlans: [],
  preventiveActions: [],
  attachments: [],
};

const SECTIONS = [
  { id: "A", label: "A. Introduction" },
  { id: "B", label: "B. RCA Team" },
  { id: "C", label: "C. Synopsis" },
  { id: "D", label: "D. Timeline" },
  { id: "E", label: "E. Witness" },
  { id: "F", label: "F. Consequences" },
  { id: "G", label: "G. Root Cause Analysis" },
  { id: "H", label: "H. Contributing Factors" },
  { id: "I", label: "I. Root Cause" },
  { id: "J", label: "J. Conclusion" },
  { id: "K", label: "K. Corrective Actions" },
  { id: "L", label: "L. Preventive Actions" },
  { id: "Attachments", label: "Attachments" },
];

// ============================================================================
// COMPONENT
// ============================================================================

const GenericRCAModal: React.FC<RCAActionModalProps> = ({
  data,
  onActionComplete,
  mode = "create",
  rcaId,
  moduleKey,
}) => {
  const { canUpdate, canCreate } = usePermissions();
  const hasUpdatePerm = canUpdate(moduleKey || INCIDENTS_MODULE);
  const hasCreatePerm = canCreate(moduleKey || INCIDENTS_MODULE);
  const isViewMode = mode === "view";

  const isReadOnlySectionA = true;
  const isReadOnlySectionB = isViewMode || !hasCreatePerm;
  const isReadOnlySectionCtoJ = isViewMode || !hasUpdatePerm;
  const canSubmit = hasCreatePerm || hasUpdatePerm;

  const [activeSection, setActiveSection] = useState<string>("A");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<GenericRCAFormData>(INITIAL_STATE);
  const [files, setFiles] = useState<File[] | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Document Viewer States
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState<{ url: string; name: string; type: string }>({ url: "", name: "", type: "" });

  const [rcaRecordId, setRcaRecordId] = useState<string | null>(rcaId || null);

  const { allPremises } = usePremisesStore();
  const { regions } = useRegionStore();

  const { uploadFileToS3, progress: s3UploadProgress, uploading: s3Uploading } = useS3Upload();

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => setFiles(acceptedFiles),
    disabled: loading || isViewMode,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    },
  });

  const isDisabled = isViewMode || submitting || uploading || s3Uploading || !canSubmit;

  useEffect(() => {
    if (rcaId) {
      setRcaRecordId(rcaId);
      loadRcaById(rcaId);
    } else if (data?.id) {
      loadRcaByIncidentId(data.id);
    }
  }, [data?.id, mode, rcaId]);

  const loadRcaById = async (id: string) => {
    setLoading(true);
    try {
      const response = await getRCAById(id);
      if (response.success && response.data) populateFormFromRca(response.data);
    } catch (error) {
      handleApiError(error, "Failed to load RCA data");
    } finally {
      setLoading(false);
    }
  };

  const loadRcaByIncidentId = async (incidentId: string) => {
    setLoading(true);
    try {
      const response = await getRCAByIncidentId(incidentId);
      if (response.success && response.data?.id) {
        setRcaRecordId(response.data.id);
        populateFormFromRca(response.data);
      } else {
        toast.error("No RCA found for this incident. It may have been created before the auto-RCA feature.");
      }
    } catch (error) {
      handleApiError(error, "Failed to load RCA data");
    } finally {
      setLoading(false);
    }
  };

  const populateFormFromRca = (d: any) => {
    const premiseId = allPremises.find((p: any) => p.premisesName === d.premisesName)?._id || d.premisesName || "";
    const regionId = regions.find((r: any) => r.regionName?.trim() === d.region?.trim())?._id || d.region || "";

    setFormData({
      organizationName: d.organizationName || "",
      branchName: d.branchName || "",
      region: regionId,
      premisesName: premiseId,
      incidentEventType: d.incidentEventType || "",
      incidentEventTypeOther: d.incidentEventTypeOther || "",
      eventDateTime: d.eventDateTime || "",
      rcaDoneDate: d.rcaDoneDate || getCurrentDate(),
      location: d.location || "",
      severity: d.severity || "",
      reportedBy: d.reportedBy || "",
      status: d.status || "Draft",
      rcaTeamMembers: d.rcaTeamMembers || [],
      incidentSynopsis: d.incidentSynopsis || "",
      observations: d.observations || "",
      summary: d.summary || "",
      identifiedCause: d.identifiedCause || "",
      timelineEvents: d.timelineEvents || [],
      witnessStatements: d.witnessStatements || [],
      consequences: d.consequences || [],
      fishboneAnalysis: d.fishboneAnalysis || INITIAL_STATE.fishboneAnalysis,
      whyWhyAnalysis: d.whyWhyAnalysis || [],
      rcaMethod: d.rcaMethod === "Fishbone" || d.rcaMethod === "Why-Why" || d.rcaMethod === "Both" ? d.rcaMethod : "Fishbone",
      contributingFactors: d.contributingFactors?.length > 0 ? d.contributingFactors : INITIAL_STATE.contributingFactors,
      rootCauseIdentified: d.rootCauseIdentified || false,
      rootCauseDescription: d.rootCauseDescription || "",
      conclusion: d.conclusion || "",
      correctiveActionPlans: d.correctiveActionPlans || [],
      preventiveActions: d.preventiveActions || [],
      attachments: d.attachments || [],
    });

    if (d.attachments?.length > 0) setExistingAttachments(d.attachments);
  };

  useEffect(() => {
    if (formData.attachments?.length > 0) setExistingAttachments(formData.attachments);
  }, [formData.attachments]);

  const handleChange = (field: keyof GenericRCAFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // RCA Team
  const handleAddTeamMember = () => handleChange("rcaTeamMembers", [...formData.rcaTeamMembers, { srNo: formData.rcaTeamMembers.length + 1, name: "", department: "", isLead: false }]);
  const handleUpdateTeamMember = (index: number, field: keyof RCATeamMember, value: any) => {
    const updated = [...formData.rcaTeamMembers];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "isLead" && value === true) updated.forEach((member, i) => { if (i !== index) member.isLead = false; });
    handleChange("rcaTeamMembers", updated);
  };
  const handleRemoveTeamMember = (index: number) => handleChange("rcaTeamMembers", formData.rcaTeamMembers.filter((_, i) => i !== index).map((m, i) => ({ ...m, srNo: i + 1 })));

  // Timeline
  const handleAddTimelineEvent = () => {
    if (formData.timelineEvents.some((a) => !a.date.trim() || !a.time.trim() || !a.event.trim())) return toast.error("Please fill all Timeline Event details.");
    handleChange("timelineEvents", [...formData.timelineEvents, { srNo: formData.timelineEvents.length + 1, date: getCurrentDate(), time: "", event: "" }]);
  };
  const handleUpdateTimelineEvent = (index: number, field: keyof TimelineEvent, value: any) => {
    const updated = [...formData.timelineEvents];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("timelineEvents", updated);
  };
  const handleRemoveTimelineEvent = (index: number) => handleChange("timelineEvents", formData.timelineEvents.filter((_, i) => i !== index).map((e, i) => ({ ...e, srNo: i + 1 })));

  // Witnesses
  const handleAddWitness = () => {
    if (formData.witnessStatements.some((a) => !a.witnessName.trim() || !a.statement.trim())) return toast.error("Please fill all Witness Statement details.");
    handleChange("witnessStatements", [...formData.witnessStatements, { witnessName: "", statement: "", contactInfo: "" }]);
  };
  const handleUpdateWitness = (index: number, field: keyof WitnessStatement, value: any) => {
    const updated = [...formData.witnessStatements];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("witnessStatements", updated);
  };
  const handleRemoveWitness = (index: number) => handleChange("witnessStatements", formData.witnessStatements.filter((_, i) => i !== index));

  // Consequences
  const handleAddConsequence = () => {
    if (formData.consequences.some((a) => !a.type.trim())) return toast.error("Please fill all Consequence details.");
    handleChange("consequences", [...(formData.consequences || []), { srNo: formData.consequences?.length ? formData.consequences.length + 1 : 1, type: "", isYes: false, remark: "" }]);
  };
  const handleUpdateConsequence = (index: number, field: keyof Consequence, value: any) => {
    const updated = [...formData.consequences];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("consequences", updated);
  };
  const handleRemoveConsequence = (index: number) => handleChange("consequences", formData.consequences.filter((_, i) => i !== index).map((c, i) => ({ ...c, srNo: i + 1 })));

  // Corrective Actions
  const handleAddCorrectiveAction = () => {
    if (formData.correctiveActionPlans.some((a) => !a.correctiveAction.trim() || !a.responsiblePersonOrTeam.trim() || !a.deadline)) return toast.error("Please fill all Corrective Action Plan details.");
    handleChange("correctiveActionPlans", [...formData.correctiveActionPlans, { srNo: formData.correctiveActionPlans.length + 1, rootCauseOrContributingFactor: "", correctiveAction: "", responsiblePersonOrTeam: "", deadline: "", status: "Pending" }]);
  };
  const handleUpdateCorrectiveAction = (index: number, field: keyof CorrectiveActionPlan, value: any) => {
    const updated = [...formData.correctiveActionPlans];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("correctiveActionPlans", updated);
  };
  const handleRemoveCorrectiveAction = (index: number) => handleChange("correctiveActionPlans", formData.correctiveActionPlans.filter((_, i) => i !== index).map((a, i) => ({ ...a, srNo: i + 1 })));

  // Preventive Actions
  const handleAddPreventiveAction = () => {
    if (formData.preventiveActions.some((a) => !a.preventiveAction.trim() || !a.responsiblePersonOrTeam.trim())) return toast.error("Please fill all Preventive Action Plan details.");
    handleChange("preventiveActions", [...formData.preventiveActions, { srNo: formData.preventiveActions.length + 1, preventiveAction: "", responsiblePersonOrTeam: "" }]);
  };
  const handleUpdatePreventiveAction = (index: number, field: keyof PreventiveActionPlan, value: any) => {
    const updated = [...formData.preventiveActions];
    updated[index] = { ...updated[index], [field]: value };
    handleChange("preventiveActions", updated);
  };
  const handleRemovePreventiveAction = (index: number) => handleChange("preventiveActions", formData.preventiveActions.filter((_, i) => i !== index).map((a, i) => ({ ...a, srNo: i + 1 })));

  // Submission
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault(); if (!isViewMode) await submitRCA(false); };
  const handleSaveDraft = async () => { if (!isViewMode) await submitRCA(true); };

  const submitRCA = async (saveAsDraft: boolean) => {
    if (!data?.id) return toast.error("Incident data is missing");
    if (!rcaRecordId) return toast.error("RCA record not found. Please close and reopen.");

    if (!saveAsDraft) {
      if (!formData.incidentSynopsis) return toast.error("Incident Synopsis is required.");
      if (formData.rcaTeamMembers.length === 0) return toast.error("At least one RCA Team Member is required.");
      if (formData.rcaTeamMembers.some((m) => !m.name.trim() || !m.department.trim())) return toast.error("Please complete all RCA Team Member details.");
      if (formData.timelineEvents.length === 0) return toast.error("At least one Timeline Event is required.");
      if (formData.timelineEvents.some((e) => !e.date || !e.time || !e.event.trim())) return toast.error("Please complete all Timeline Event details.");
      if (!formData.consequences || formData.consequences.length === 0) return toast.error("At least one Consequence is required.");
      if (formData.consequences.some((c) => c.isYes && !c.remark?.trim())) return toast.error("Remarks are required for all 'Yes' answers in Consequences.");
      if (formData.rcaMethod === "Why-Why" && formData.whyWhyAnalysis.length === 0) return toast.error("Please provide at least one entry for Why-Why Analysis.");
      if (formData.rcaMethod === "Fishbone") {
        const { man, material, machine, system, management } = formData.fishboneAnalysis;
        if (man.length === 0 && material.length === 0 && machine.length === 0 && system.length === 0 && management.length === 0) return toast.error("Please provide at least one entry in Fishbone Analysis.");
      }
      if (!formData.contributingFactors.some((f) => f.isApplicable)) return toast.error("At least one Contributing Factor must be marked as applicable (Yes).");
      if (!formData.rootCauseDescription) return toast.error("Root Cause is required.");
      if (!formData.conclusion) return toast.error("Conclusion is required.");
      if (formData.correctiveActionPlans.length === 0) return toast.error("At least one Corrective Action Plan must be added.");
      if (formData.correctiveActionPlans.some((a) => !a.correctiveAction.trim() || !a.responsiblePersonOrTeam.trim() || !a.deadline)) return toast.error("Please complete all Corrective Action Plan details.");
    }

    try {
      setSubmitting(true); setUploading(true);
      let uploadedAttachments: any[] = [...existingAttachments];
      if (files && files.length > 0) {
        setUploadProgress(0);
        const uploadPromises = files.map(async (file, index) => {
          const url = await uploadFileToS3(file);
          setUploadProgress(((index + 1) / files.length) * 100);
          return { url, name: file.name, originalName: file.name, size: file.size, mimeType: file.type, uploadedAt: new Date().toISOString() };
        });
        uploadedAttachments = [...uploadedAttachments, ...(await Promise.all(uploadPromises))];
      }

      const selectedPremise = allPremises.find((p: any) => p._id === formData.premisesName);
      const selectedRegion = regions.find((r: any) => r._id === formData.region);

      const rcaPayload = {
        ...formData,
        incident: data.id,
        premisesName: selectedPremise?.premisesName || formData.premisesName,
        region: selectedRegion?.regionName?.trim() || formData.region,
        status: saveAsDraft ? "In Progress" : "Completed",
        completionDate: saveAsDraft ? "" : getCurrentISODate(),
        attachments: uploadedAttachments,
      };

      const response = await updateRCA(rcaRecordId, rcaPayload);
      toast.success(saveAsDraft ? "RCA saved as draft successfully" : "RCA submitted successfully");

      if (!saveAsDraft) window.dispatchEvent(new Event("incidentResolved"));
      if (response.success) setTimeout(() => onActionComplete?.(), 500);

    } catch (error) {
      handleApiError(error, "Failed to save RCA");
    } finally {
      setSubmitting(false); setUploading(false); setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0fa9a6]" />
        <span className="ml-3 text-lg">Loading RCA data...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-6 py-3 border-b bg-gray-50 overflow-x-auto flex-shrink-0 shadow-sm">
        <div className="flex gap-2 min-w-max">
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => { setActiveSection(section.id); setTimeout(() => { document.getElementById("genericRcaForm")?.scrollTo(0, 0); }, 100); }}
              className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all ${activeSection === section.id ? "bg-[#0fa9a6] text-white shadow-md" : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"}`}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} id="genericRcaForm" className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
        {activeSection === "A" && <RCAIntroductionSection formData={formData} onChange={handleChange} readOnly={isReadOnlySectionA} regions={regions} premises={allPremises} />}
        {activeSection === "B" && <RCATeamSection members={formData.rcaTeamMembers} onAdd={handleAddTeamMember} onUpdate={handleUpdateTeamMember} onRemove={handleRemoveTeamMember} readOnly={isReadOnlySectionB} />}
        {activeSection === "C" && <RCASynopsisSection formData={formData} onChange={handleChange} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "D" && <RCATimelineSection events={formData.timelineEvents} onAdd={handleAddTimelineEvent} onUpdate={handleUpdateTimelineEvent} onRemove={handleRemoveTimelineEvent} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "E" && <RCAWitnessSection witnesses={formData.witnessStatements} onAdd={handleAddWitness} onUpdate={handleUpdateWitness} onRemove={handleRemoveWitness} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "F" && <RCAConsequencesSection consequences={formData.consequences} onAdd={handleAddConsequence} onUpdate={handleUpdateConsequence} onRemove={handleRemoveConsequence} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "G" && <RCARootCauseAnalysisSection rcaMethod={formData.rcaMethod} fishboneAnalysis={formData.fishboneAnalysis} whyWhyAnalysis={formData.whyWhyAnalysis} rootCauseDescription={formData.rootCauseDescription} onChange={handleChange} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "H" && <RCAContributingFactorsSection factors={formData.contributingFactors} onChange={(factors) => handleChange("contributingFactors", factors)} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "I" && <RCARootCauseSection identified={formData.rootCauseIdentified} description={formData.rootCauseDescription} onChange={handleChange} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "J" && <RCAConclusionSection conclusion={formData.conclusion} onChange={(value) => handleChange("conclusion", value)} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "K" && <RCACorrectiveActionsSection actions={formData.correctiveActionPlans} onAdd={handleAddCorrectiveAction} onUpdate={handleUpdateCorrectiveAction} onRemove={handleRemoveCorrectiveAction} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "L" && <RCAPreventiveActionsSection actions={formData.preventiveActions} onAdd={handleAddPreventiveAction} onUpdate={handleUpdatePreventiveAction} onRemove={handleRemovePreventiveAction} readOnly={isReadOnlySectionCtoJ} />}
        {activeSection === "Attachments" && (
          <RCAAttachmentsSection
            files={files} onFilesChange={setFiles} existingAttachments={existingAttachments}
            onRemoveExistingAttachment={(index) => {
              const updated = existingAttachments.filter((_, i) => i !== index);
              setExistingAttachments(updated); handleChange("attachments", updated);
            }}
            onPreviewFile={(attachment) => {
              const url = attachment.url || attachment;
              const name = attachment.filename || attachment.name || attachment.originalName || "document";
              const type = name.split('.').pop()?.toLowerCase() || "";
              const presignedUrl = getSignedUrlForS3(url as string);
              if (['pdf', 'png', 'jpg', 'jpeg', 'gif'].includes(type) || type.match(/(jpeg|jpg|png|gif|pdf)/i)) {
                setFileUrl({ url: presignedUrl, name, type }); setIsDocumentViewerOpen(true);
              } else { window.open(presignedUrl, "_blank"); }
            }}
            uploading={uploading || s3Uploading} uploadProgress={uploadProgress || s3UploadProgress} disabled={loading || isReadOnlySectionCtoJ} getRootProps={getRootProps} getInputProps={getInputProps}
          />
        )}
      </form>

      {(!isViewMode && canSubmit) || isViewMode ? (
        <div className="bg-white border-t-2 border-gray-200 px-6 py-3 flex justify-between items-center shadow-lg flex-shrink-0">
          <div>
            {isViewMode && <GenericRCAPrintPDF formData={formData} incidentData={data} buttonClass="flex items-center gap-2 px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg text-sm transition-colors shadow-md" />}
          </div>
          <div className="flex items-center gap-3">
            {!isViewMode && canSubmit && (
              <>
                {(submitting || uploading || s3Uploading) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0fa9a6]" />
                    <span>{uploading || s3Uploading ? `Uploading... ${uploadProgress || s3UploadProgress}%` : "Processing..."}</span>
                  </div>
                )}
                <button type="button" onClick={handleSaveDraft} className="px-6 py-2.5 bg-white text-[#0fa9a6] rounded-lg hover:bg-teal-50 font-medium transition-colors border-2 border-[#0fa9a6]" disabled={isDisabled}>Save as Draft</button>
                <button type="submit" form="genericRcaForm" disabled={isDisabled} className="px-6 py-2.5 bg-[#0fa9a6] text-white rounded-lg hover:bg-[#0d8f8c] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-md hover:shadow-lg">
                  {submitting ? "Submitting..." : "Submit & Complete"}
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}

      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[110000] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button type="button" onClick={() => setIsDocumentViewerOpen(false)} className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {fileUrl.url ? (
                fileUrl.type === "pdf" ? <PdfViewer fileUrl={fileUrl.url} /> : <DocumentViewer file={fileUrl.url} name={fileUrl.name} />
              ) : (
                <div className="flex justify-center items-center h-full"><p>No document to display</p></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericRCAModal;
